import React, { useEffect, useMemo, useState } from 'react';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnDropZone } from './ColumnDropZone';
import ListSectionHeader from './ListSectionHeader';
import { SortableTaskRow } from './TaskListRow';
import './ListSection.css';

const NO_TASKS = [];

/** List-view counterpart of Column (Boards Redesign card 6.1): one card per
 *  list with a 44 px header (collapse chevron, tint badge, name, done/total,
 *  progress, Add task, ⋯) over its rows. Same `group:`/`task:`/`col:`
 *  sortable ids as the kanban view, so the shared applyDragMove handles
 *  reordering rows, moving them between sections, and reordering the
 *  sections themselves. A collapsed section keeps its (empty) drop zone, so
 *  a row can still be dropped onto it — it lands at the end of that list. */
function ListSection({ group, index = 0, progress, showProgress = true, tasks, collapsed = false, onToggleCollapsed, filtering = false, dragDisabled = false, selectedTaskId = null, onOpenTask, onAddTask, onDeleteGroup, onRenameGroup, onChangeTask, onDeleteTask, justCreatedRef, justCreatedGroupsRef, memberById, assignCtx, palette }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `group:${group.id}`,
  });
  const [editing, setEditing] = useState(false);
  // A list created just now (via "Add list") starts in rename mode; it removes
  // itself from the set so re-mounting an existing list never steals focus.
  useEffect(() => {
    if (justCreatedGroupsRef?.current?.has(group.id)) {
      justCreatedGroupsRef.current.delete(group.id);
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const shownTasks = collapsed ? NO_TASKS : tasks;
  const itemIds = useMemo(() => shownTasks.map((t) => `task:${t.id}`), [shownTasks]);
  const total = progress?.total ?? tasks.length;
  return (
    <section
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className={`task-board-list-section${collapsed ? ' task-board-list-section--collapsed' : ''}`}
      aria-label={group.name || 'Unnamed list'}
      data-group-id={group.id}
    >
      <ListSectionHeader
        group={group}
        index={index}
        progress={progress}
        showProgress={showProgress}
        collapsed={collapsed}
        onToggleCollapsed={() => onToggleCollapsed?.(group.id)}
        editing={editing}
        setEditing={setEditing}
        onRename={onRenameGroup}
        onDelete={() => onDeleteGroup(group)}
        onAddTask={() => onAddTask(group.id)}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <ColumnDropZone groupId={group.id} className="task-board-list-rows">
          {shownTasks.map((t) => (
            <SortableTaskRow key={t.id} task={t} selected={t.id === selectedTaskId} dragDisabled={dragDisabled} onOpenTask={onOpenTask} onChangeTask={onChangeTask} onDeleteTask={onDeleteTask} justCreatedRef={justCreatedRef} memberById={memberById} assignCtx={assignCtx} palette={palette} />
          ))}
          {!collapsed && tasks.length === 0 && (
            <div className="task-board-list-empty">
              {total === 0 ? 'No tasks yet.' : filtering ? 'No tasks match the current filters.' : 'Completed tasks are hidden.'}
            </div>
          )}
        </ColumnDropZone>
      </SortableContext>
    </section>
  );
}

// Memoised (big boards): ListView hands each section a stable task array and
// progress (useGroupTasks), so a drag step re-renders only the sections it touched.
export default React.memo(ListSection);
